import { useEffect, useMemo, useState } from "react";
import {
	Box,
	Button,
	CircularProgress,
	FormControl,
	InputLabel,
	MenuItem,
	Paper,
	Select,
	Typography,
	Checkbox,
	FormControlLabel,
	Grid,
	Switch,
} from "@mui/material";

import {
	fetchData,
	FetchDataFailureAction,
	FetchDataSuccessAction,
} from "../../../store/actions";
import { getBreachDueDatesData, jiraChartResponseProfile } from "../../../Service/Apis";
import { ThunkDispatch } from "@reduxjs/toolkit";
import { RootState } from "../../../store/Store";
import { useDispatch, useSelector } from "react-redux";
import MonthButtonGroup from "../../utilization/MonthsButtonGroup";
import { useCustomToast } from "../../Methods/SnackBar";
import BarChartComponent from "../../charts/BarChartComponent";
import { JiraEmployeeBarTooltip } from "../../utilization/utils";
import { isEmployeeAccessRole } from "../../../utils/accessRole";
import {
	barChartSeriesNames,
	jiraChartBlobDownload,
	resolveJiraExportEmployeeIds,
} from "../chartExport";

export type BreachDueDatesReportChartProps = {
	employeeId?: string;
	controlledMonth?: string;
	hideMonthButtons?: boolean;
	tooltipScopedSeriesName?: string;
};
const BreachDueDatesReportChart = (props: BreachDueDatesReportChartProps = {} ) => {
	const {
		employeeId,
		controlledMonth,
		hideMonthButtons,
		tooltipScopedSeriesName,
	} = props;
	const dispatch2: ThunkDispatch<
		RootState,
		null,
		FetchDataSuccessAction | FetchDataFailureAction
	> = useDispatch();
	const [breachDueDatesReportData, setBreachDueDatesReportData] = useState<any>(
		[]
	);
	const [teamName, setTeamName] = useState<string>("");
	const [month, setMonth] = useState<string>("1");
	const [selectedFormat, setSelectedFormat] = useState<string>("excel");
	const showToast = useCustomToast();
	const [loading, setLoading] = useState(false);
	const [downloading, setDownloading] = useState(false);
	const [selectedEmployees, setSelectedEmployees] = useState<string[]>([]);
	const [selectAll, setSelectAll] = useState(true);

	const nrole = useSelector((state: any) => state.auth.selectedRole);
	const paramsData = useMemo(
		() => ({
			...jiraChartResponseProfile,
			role: nrole ?? "Employee",
			...(employeeId ? { employee_ids: [employeeId] } : {}),
		}),
		[nrole, employeeId]
	);

	const fetchBreachDueDatesReportData = async (selectedMonth: string) => {
		try {
			setLoading(true);
			const res = await dispatch2(
				fetchData(`${getBreachDueDatesData}${selectedMonth}`, paramsData)
			);
			setMonth(selectedMonth);
			setBreachDueDatesReportData(res);

			// Set all employee names as selected by default
			const allEmployeeNames = res?.teams
				? res?.teams?.flatMap((issue: any) =>
						issue.employees.map((assignee: any) => assignee.employee_name)
				  ) || []
				: res?.issues?.flatMap((issue: any) =>
						issue.assignees.map((assignee: any) => assignee.assignee_name)
				  ) || [];
			setSelectedEmployees(Array.from(new Set(allEmployeeNames))); // Ensure unique names
		} catch (error) {
			console.error(error);
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		fetchBreachDueDatesReportData(controlledMonth ?? "1");
	}, [controlledMonth, employeeId, nrole]);

	useEffect(() => {
		if (employeeId && tooltipScopedSeriesName) {
			setSelectedEmployees([tooltipScopedSeriesName]);
			setSelectAll(true);
		}
	}, [employeeId, tooltipScopedSeriesName, breachDueDatesReportData]);

	useEffect(() => {
		if (!employeeId || !breachDueDatesReportData?.teams?.length) return;
		const t = breachDueDatesReportData.teams.find(
			(x: any) => x.team_name === "All"
		);
		if (t) setTeamName("All");
	}, [employeeId, breachDueDatesReportData]);

	useEffect(() => {
		if (!breachDueDatesReportData?.teams?.length) return;
		const selectedTeam =
			breachDueDatesReportData.teams.find(
				(team: any) => team.team_name === teamName
			) || breachDueDatesReportData.teams[0];
		if (!selectedTeam) return;

		if (!teamName) {
			setTeamName(selectedTeam.team_name);
		}

		const teamEmployeeNames =
			selectedTeam.employees?.map((e: any) => e.employee_name) ?? [];
		if (!selectedEmployees.every((name) => teamEmployeeNames.includes(name))) {
			setSelectedEmployees(teamEmployeeNames);
			setSelectAll(true);
		}
	}, [breachDueDatesReportData, teamName]);

	const handleMonthChange = (selectedMonth: string) => {
		fetchBreachDueDatesReportData(selectedMonth);
	};

	const buttonData = [
		{ label: "1M", onClick: () => handleMonthChange("1") },
		{ label: "3M", onClick: () => handleMonthChange("3") },
		{ label: "6M", onClick: () => handleMonthChange("6") },
		{ label: "1Y", onClick: () => handleMonthChange("12") },
	];

	const computedData = useMemo(() => {
		// Handle data for employee role (second format)
		if (
			Array.isArray(
				breachDueDatesReportData?.issues || breachDueDatesReportData
			)
		) {
			const sourceData =
				breachDueDatesReportData?.issues || breachDueDatesReportData;

			const data = sourceData.flatMap((issue: any) =>
				issue.assignees && issue.assignees.length > 0
					? issue.assignees.map((assignee: any) => ({
							assignee_name: assignee?.assignee_name,
							week: issue?.week_end,
							issue_count: assignee?.issue_count,
					  }))
					: [
							{
								week: issue?.week_end,
								assignee_name: "No Assignees",
								issue_count: 0,
							},
					  ]
			);

			const groupedData = data.reduce((acc: any, curr: any) => {
				const weekData = acc.find((item: any) => item.week === curr.week) || {
					week: curr.week,
				};
				if (curr.assignee_name) {
					weekData[curr.assignee_name] = curr.issue_count;
				}
				if (!acc.includes(weekData)) {
					acc.push(weekData);
				}
				return acc;
			}, []);

			return groupedData;
		}

		// Handle data for other roles (first format with teams)
		if (!breachDueDatesReportData || !breachDueDatesReportData.teams) {
			console.error("No valid data found");
			return [];
		}

		const selectedTeam =
			breachDueDatesReportData.teams.find(
				(team: any) => team.team_name === teamName
			) || breachDueDatesReportData.teams[0];

		setTeamName(selectedTeam?.team_name);

		if (selectedTeam) {
			const data = selectedTeam.issues.flatMap((issue: any) =>
				issue.assignees && issue.assignees.length > 0
					? issue.assignees.map((assignee: any) => ({
							assignee_name: assignee?.assignee_name,
							week: issue?.week_end,
							issue_count: assignee?.issue_count,
					  }))
					: [
							{
								week: issue?.week_end,
								assignee_name: "No Assignees",
								issue_count: 0,
							},
					  ]
			);

			const groupedData = data.reduce((acc: any, curr: any) => {
				const weekData = acc.find((item: any) => item.week === curr.week) || {
					week: curr.week,
				};
				if (curr.assignee_name) {
					weekData[curr.assignee_name] = curr.issue_count;
				}
				if (!acc.includes(weekData)) {
					acc.push(weekData);
				}
				return acc;
			}, []);

			return groupedData;
		}
		return [];
	}, [breachDueDatesReportData, teamName]);

	const chartEmployeeNames = useMemo(
		() => barChartSeriesNames(computedData),
		[computedData]
	);

	const onDownload = async () => {
		try {
			setDownloading(true);
			const isEmployeeRole =
				(nrole ?? "Employee").toLowerCase() === "employee";

			let selectedTeam: any = null;
			if (!isEmployeeRole) {
				if (!teamName) {
					throw new Error("Please select a team");
				}
				selectedTeam = breachDueDatesReportData.teams?.find(
					(team: any) => team.team_name === teamName
				);
				if (!selectedTeam) {
					throw new Error("Selected team not found");
				}
			}

			const employeeIds = resolveJiraExportEmployeeIds({
				selectedEmployees,
				chartEmployeeNames,
				teamEmployees: selectedTeam?.employees,
				rowsForIdFallback: [],
			});

			const baseParams: Record<string, unknown> = {
				...paramsData,
				download: selectedFormat,
			};
			if (employeeIds) {
				baseParams.employee_ids = employeeIds;
			}
			if (!isEmployeeRole && selectedTeam) {
				baseParams.team_id = selectedTeam.team_id;
			}

			const ext =
				selectedFormat === "excel" ? "xlsx" : selectedFormat;
			const filename = !isEmployeeRole
				? `breached-due-dates-${teamName.replace(/\s+/g, "-")}.${ext}`
				: `breached-due-dates.${ext}`;

			await jiraChartBlobDownload(
				`${getBreachDueDatesData}${month}`,
				baseParams,
				filename,
				showToast
			);
		} catch (error: any) {
			showToast(error?.message ?? String(error), "error", true);
		} finally {
			setDownloading(false);
		}
	};

	// Helper function to get all employee names
	const getAllEmployeeNames = () => {
		return breachDueDatesReportData?.teams
			? breachDueDatesReportData?.teams?.flatMap((issue: any) =>
					issue.employees.map((assignee: any) => assignee.employee_name)
			  ) || []
			: breachDueDatesReportData?.issues?.flatMap((issue: any) =>
					issue.assignees.map((assignee: any) => assignee.assignee_name)
			  ) || [];
	};

	// Update handleEmployeeSelection to manage selectAll state
	const handleEmployeeSelection = (employeeName: string) => {
		const newSelectedEmployees = selectedEmployees.includes(employeeName)
			? selectedEmployees.filter((name) => name !== employeeName)
			: [...selectedEmployees, employeeName];

		setSelectedEmployees(newSelectedEmployees);

		const uniqueEmployees = barChartSeriesNames(computedData);

		if (newSelectedEmployees.length === uniqueEmployees.length) {
			setSelectAll(true);
		}
		if (newSelectedEmployees.length === 0) {
			setSelectAll(false);
		}
	};

	// Replace handleSwitchChange with simpler logic
	const handleSwitchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		const checked = event.target.checked;
		setSelectAll(checked);

		if (checked) {
			// Select all employees
			setSelectedEmployees(getAllEmployeeNames());
		} else {
			// Deselect all employees
			setSelectedEmployees([]);
		}
	};

	return (
		<>
			<Paper sx={{ m: 2, p: 4 }}>
				<Box display="flex" justifyContent="space-between" alignItems="center">
					<Box
						display="flex"
						justifyContent="center"
						alignItems="center"
						flexDirection="column"
						gap={2}
					>
						<Typography variant="h4">Breached Due Dates Chart</Typography>
						{!hideMonthButtons && (
							<MonthButtonGroup buttonData={buttonData} />
						)}
					</Box>
					<Box
						display="flex"
						justifyContent="center"
						alignItems="center"
						flexDirection="column"
						gap={2}
					>
						{breachDueDatesReportData?.teams && !employeeId && (
							<FormControl
								size="small"
								sx={{ minWidth: 220, width: "100%", maxWidth: 320 }}
							>
								<InputLabel id="breach-due-dates-team-select-label">
									Select Team
								</InputLabel>
								<Select
									labelId="breach-due-dates-team-select-label"
									id="breach-due-dates-team-select"
									value={teamName}
									label="Select Team"
									onChange={(e) => setTeamName(e.target.value)}
								>
									{breachDueDatesReportData?.teams?.map((team: any) => (
										<MenuItem key={team.team_id} value={team.team_name}>
											{team.team_name}
										</MenuItem>
									))}
								</Select>
							</FormControl>
						)}

						<Box
							display="flex"
							justifyContent="center"
							gap={2}
							alignItems="center"
							flexWrap="wrap"
						>
							<FormControl size="small" sx={{ minWidth: 120 }}>
								<InputLabel id="breach-due-dates-export-format-label">
									Format
								</InputLabel>
								<Select
									labelId="breach-due-dates-export-format-label"
									label="Format"
									value={selectedFormat}
									onChange={(e) =>
										setSelectedFormat(e.target.value)
									}
								>
									<MenuItem value="excel">
										Excel (.xlsx)
									</MenuItem>
									<MenuItem value="csv">CSV</MenuItem>
									<MenuItem value="json">JSON</MenuItem>
								</Select>
							</FormControl>
							{downloading ? (
								<Box
									display="flex"
									justifyContent="center"
									alignItems="center"
									gap={2}
								>
									<CircularProgress size="1rem" />
									<Typography>Downloading...</Typography>
								</Box>
							) : (
								<Button
									variant="outlined"
									size="small"
									onClick={onDownload}
								>
									Export
								</Button>
							)}
						</Box>
					</Box>
				</Box>
				{!employeeId && !isEmployeeAccessRole(nrole) && (
					<Box
						display="flex"
						justifyContent="flex-end"
						alignItems="center"
						gap={2}
						m={2}
						width="100%"
					>
						<Typography>Select All Employees</Typography>
						<Switch
							checked={selectAll}
							onChange={handleSwitchChange}
							color="primary"
						/>
					</Box>
				)}
				{loading ? (
					<Box
						display="flex"
						justifyContent="center"
						alignItems="center"
						width="100%"
						height={200}
					>
						<CircularProgress />
					</Box>
				) : (
					<Box
						display="flex"
						justifyContent="center"
						alignItems="center"
						flexDirection="column"
					>
						<BarChartComponent
							data={computedData || []}
							YAxisLabel="Issue Count"
							selectedEmployees={selectedEmployees}
							onEmployeeSelection={handleEmployeeSelection}
							tooltipContent={
								<JiraEmployeeBarTooltip
									scopedSeriesName={tooltipScopedSeriesName}
								/>
							}
						/>
					</Box>
				)}
			</Paper>
		</>
	);
};

export default BreachDueDatesReportChart;
